// (2) Practice Mode page: Topic Picker → Pre-session briefing → question flow.
// Practice results never affect Exam Mode bins (separate PracticeResult table).
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation, useQuery } from '@tanstack/react-query';
import { apiGet, apiPost } from '@/lib/api';
import { boardGradeLabel, decodeClaims } from '@/lib/auth';
import type {
  PracticeAttemptResult,
  PracticeSessionResponse,
  PracticeTopic,
  SubjectDTO,
} from '@/types';
import { TopicPicker } from '@/components/practice/TopicPicker';
import { PreSessionBriefing } from '@/components/practice/PreSessionBriefing';
import { PracticeAnswerCard } from '@/components/practice/PracticeAnswerCard';
import { StudentPage, StudentPageHeader } from '@/components/ds';

type Stage = 'pick' | 'brief' | 'session';

export function PracticeModePage() {
  const navigate = useNavigate();
  const claims = decodeClaims();
  const [stage, setStage] = useState<Stage>('pick');
  const [topic, setTopic] = useState<PracticeTopic | null>(null);
  const [session, setSession] = useState<PracticeSessionResponse | null>(null);
  const [qIndex, setQIndex] = useState(0);
  // Questions already shown this session, so "Try another on this topic" can
  // reach for something unseen instead of retracing "Next question"'s path.
  const [seenIndices, setSeenIndices] = useState<Set<number>>(() => new Set([0]));

  // Shares the React Query cache with Home/Dashboard's identical query.
  const subjectsQuery = useQuery({
    queryKey: ['subjects', claims?.board, claims?.grade],
    enabled: Boolean(claims?.board && claims?.grade),
    queryFn: () => apiGet<SubjectDTO[]>(`/subjects?board=${claims!.board}&grade=${claims!.grade}`),
  });
  const subjectName = (id: string | undefined) =>
    subjectsQuery.data?.find((s) => s.id === id)?.name;

  const startSession = useMutation({
    mutationFn: (topicId: string) =>
      apiPost<PracticeSessionResponse>('/practice/sessions', { topicId }),
    onSuccess: (data) => {
      navigate(`/exam/${data.session.id}`);
    },
  });

  const question = session?.questions[qIndex];

  function resetProgress() {
    setQIndex(0);
    setSeenIndices(new Set([0]));
  }

  function returnToTopics() {
    setStage('pick');
    setTopic(null);
    setSession(null);
    resetProgress();
  }

  function returnToBrief() {
    setStage('brief');
    setSession(null);
    resetProgress();
  }

  function goToNext() {
    if (!session) return;
    const next = qIndex + 1;
    setSeenIndices((prev) => new Set(prev).add(next));
    setQIndex(next);
  }

  // Every index that "Try another" is allowed to land on: not the current
  // question, and — whenever "Next question" is offered — not its target
  // either, so the two controls can never resolve to the same question.
  function anotherCandidates(total: number, current: number, hasNext: boolean): number[] {
    const excluded = new Set(hasNext ? [current, current + 1] : [current]);
    return Array.from({ length: total }, (_, i) => i).filter((i) => !excluded.has(i));
  }

  // Picks a different question on the same topic, unseen ones first. Only
  // called when anotherCandidates() is non-empty (see onAnother below).
  function goToAnother() {
    if (!session) return;
    const hasNext = qIndex < session.questions.length - 1;
    const candidates = anotherCandidates(session.questions.length, qIndex, hasNext);
    if (candidates.length === 0) return;
    const unseen = candidates.filter((i) => !seenIndices.has(i));
    const pool = unseen.length > 0 ? unseen : candidates;
    const next = pool[Math.floor(Math.random() * pool.length)];
    setSeenIndices((prev) => new Set(prev).add(next));
    setQIndex(next);
  }

  async function submitAnswer(selectedIdx: number): Promise<PracticeAttemptResult> {
    if (!session || !question) throw new Error('No active question');
    return apiPost<PracticeAttemptResult>(`/practice/sessions/${session.session.id}/attempts`, {
      questionId: question.id,
      answer: String(selectedIdx),
      answerMethod: 'OPTION',
      secondsTaken: 0,
    });
  }

  return (
    <StudentPage>
      <StudentPageHeader
        breadcrumbs={
          stage !== 'pick' && topic
            ? [
                { label: 'Practice by topic', onClick: returnToTopics },
                { label: topic.name, onClick: stage === 'session' ? returnToBrief : undefined },
                ...(stage === 'session'
                  ? [{ label: question ? `Question ${qIndex + 1}` : 'Complete' }]
                  : []),
              ]
            : undefined
        }
        title={
          stage === 'brief' && topic
            ? `Ready to practise ${topic.name}?`
            : stage === 'session' && session
              ? question
                ? `Question ${qIndex + 1} of ${session.questions.length}`
                : 'Practice complete'
              : 'Practice by topic'
        }
        subtitle={boardGradeLabel(claims)}
      />

      <div className="w-full">
        {stage === 'pick' && (
          <TopicPicker
            onPick={(t) => {
              setTopic(t);
              setStage('brief');
            }}
          />
        )}

        {stage === 'brief' && topic && (
          <div className="flex flex-col gap-3">
            <PreSessionBriefing
              topic={topic}
              onStart={() => startSession.mutate(topic.topicId)}
            />
            <button
              onClick={() => navigate(`/learn/basics?fromTopicId=${topic.topicId}`)}
              className="text-center text-sm font-medium"
              style={{ color: 'var(--ink-2)' }}
            >
              Not sure of the basics? → Strengthen earlier topics first
            </button>
          </div>
        )}

        {stage === 'session' && session && question && (
          <div>
            <PracticeAnswerCard
              key={question.id}
              question={question}
              subjectLabel={subjectName(session.topic.subjectId)}
              topicLabel={session.topic.name}
              onSubmit={submitAnswer}
              onNext={qIndex < session.questions.length - 1 ? goToNext : undefined}
              onAnother={
                anotherCandidates(
                  session.questions.length,
                  qIndex,
                  qIndex < session.questions.length - 1,
                ).length > 0
                  ? goToAnother
                  : undefined
              }
              onScaffold={() => navigate(`/learn/basics/${question.subtopicId}`)}
              nextLabel={qIndex < session.questions.length - 1 ? 'Next question' : 'Done'}
            />
          </div>
        )}

        {stage === 'session' && session && !question && (
          <div
            className="rounded-2xl p-6 text-center"
            style={{ background: 'var(--surface-card)', border: '1px solid var(--border-default)' }}
          >
            <p className="font-bold" style={{ color: 'var(--text-body)' }}>
              Practice complete 🎉
            </p>
            <button
              onClick={returnToTopics}
              className="mt-3 rounded-xl px-5 py-2.5 font-bold text-white"
              style={{ background: 'var(--action-bg)' }}
            >
              Practise another topic
            </button>
          </div>
        )}
      </div>
    </StudentPage>
  );
}
